import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { suggestionGrade, byGrade, type VerdictInput } from '../fleet/suggestionGrade';
import {
  clearIndication,
  fetchAvailability,
  indicateCar,
  toErrorMessage,
  type Availability,
  type AvailabilityCandidate,
  type InquiryDetail,
} from './inquiriesApi';

const GRADE_COLOR = { ok: 'success', caution: 'warning', unknown: 'default', late: 'error' } as const;

const km = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${Math.round(n)} km`);
const pln = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `~${Math.round(n).toLocaleString('pl-PL')} zł`;

/** Ocena kandydata — te same cztery grupy co rekomendacje na karcie pojazdu. */
export function candidateGrade(c: AvailabilityCandidate) {
  return suggestionGrade(c as unknown as VerdictInput);
}

/**
 * Dostępność aut dla zapytania (makieta CS-06; 26.10.2026, P3,
 * `clientInquiries`). Ten sam silnik co rekomendacje pojazdów, policzony dla
 * miejsc i okna zapytania — bez zapisu czegokolwiek. „Wskaż wstępnie” to
 * znacznik CS: nie blokuje auta i nie jest wariantem; obsadza Master FTL po
 * zapisie zlecenia. Auto, które nie zdąży, nie da się wskazać.
 */
export const InquiryAvailability = ({
  detail,
  onChange,
}: {
  detail: InquiryDetail;
  onChange: (d: InquiryDetail) => void;
}) => {
  const [data, setData] = useState<Availability | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const indicated = detail.inquiry.indicated;

  const run = () =>
    void (async () => {
      setBusy(true);
      setError(null);
      try {
        setData(await fetchAvailability(detail.id));
      } catch (e) {
        setError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();

  const act = (fn: () => Promise<InquiryDetail>) =>
    void fn()
      .then(onChange)
      .catch((e) => setError(toErrorMessage(e)));

  const result = data?.result;
  const rows = result ? byGrade(result.candidates as unknown as Array<AvailabilityCandidate & VerdictInput>) : [];
  const skippedCount = result?.skipped.length ?? 0;

  return (
    <SectionCard title="Dostępność aut">
      <Box data-testid="inquiry-availability">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Button variant="outlined" onClick={run} disabled={busy} data-testid="inquiry-availability-run">
            {data ? 'Przelicz' : 'Sprawdź dostępność aut'}
          </Button>
          {busy && <CircularProgress size={20} />}
          {indicated && (
            <Chip
              color="warning"
              variant="outlined"
              label={`Wskazane wstępnie: ${indicated.carName ?? indicated.carId} · czeka na FTL`}
              onDelete={detail.actions.indicate ? () => act(() => clearIndication(detail.id)) : undefined}
              data-testid="inquiry-indicated"
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Ten sam rachunek co rekomendacje pojazdów — dolot, zapas, dowiezienie, koszt. Wskazanie wstępne nie blokuje
            auta.
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {data?.refused && (
          <Alert severity="info" data-testid="inquiry-availability-refused">
            {data.refused}
          </Alert>
        )}
        {result && rows.length === 0 && !data?.refused && (
          <Typography variant="body2" color="text.secondary">
            Żadne auto nie przeszło warunków.
          </Typography>
        )}
        {rows.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ocena</TableCell>
                  <TableCell>Pojazd</TableCell>
                  <TableCell>Wolny od · skąd</TableCell>
                  <TableCell>Dolot</TableCell>
                  <TableCell>Zapas</TableCell>
                  <TableCell>Koszt dolotu</TableCell>
                  <TableCell>Uwagi</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => {
                  const grade = candidateGrade(c);
                  const elsewhere = data?.indicatedElsewhere?.[c.carId] ?? [];
                  const variants = (c.candidateFor ?? []).map((v) => v.externalId ?? `#${v.userOfferId}`);
                  const notes = [
                    ...(c.busyWith ? [`zajęty: ${c.busyWith.externalId ?? 'zlecenie'}${c.busyWith.until ? ` do ${dateTimeNoSeconds(c.busyWith.until)}` : ''}`] : []),
                    ...(variants.length > 0 ? [`wariant zleceń: ${variants.join(', ')}`] : []),
                    ...(elsewhere.length > 0 ? [`wskazany w zapytaniu ${elsewhere.join(', ')}`] : []),
                    ...(c.ostrzezenia ?? []).slice(0, 2),
                  ];
                  return (
                    <TableRow key={c.carId} data-testid="inquiry-availability-row">
                      <TableCell>
                        <Tooltip title={grade.reasons.join(' · ') || ''}>
                          <Chip size="small" color={GRADE_COLOR[grade.grade]} label={grade.label} />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <b>{c.registrationNumber ?? c.makeAndModel ?? `#${c.carId}`}</b>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {[c.driverName, c.ftlCaretaker].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {c.position ? dateTimeNoSeconds(c.position.freeFrom) : '—'}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {c.position?.fromOrder ? `po zleceniu ${c.position.fromOrder}` : (c.position?.zrodlo ?? '')}
                        </Typography>
                      </TableCell>
                      <TableCell>{km(c.accessRoute?.km ?? c.accessDistance?.roadKm)}</TableCell>
                      <TableCell>{Number.isFinite(c.spareHours) ? `${c.spareHours.toFixed(1)} h` : '—'}</TableCell>
                      <TableCell>{pln(c.accessCost?.totalPln)}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="caption">{notes.join(' · ') || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        {indicated?.carId === c.carId ? (
                          <Chip size="small" color="warning" label="wskazane" />
                        ) : (
                          detail.actions.indicate && (
                            <Button
                              size="small"
                              disabled={grade.grade === 'late'}
                              title={grade.grade === 'late' ? 'Auto nie zdąży w oknie klienta' : undefined}
                              onClick={() => act(() => indicateCar(detail.id, c.carId))}
                              data-testid="inquiry-indicate"
                            >
                              Wskaż wstępnie
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {result && skippedCount > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }} data-testid="inquiry-availability-skipped">
            Pominięto {skippedCount}:{' '}
            {Object.entries(result.skippedSummary ?? {})
              .map(([k, n]) => `${result.skippedLabels?.[k] ?? k} (${n})`)
              .join(', ')}
            . Koszt to szacunek.
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
};
